import React, { createContext, useContext, useState, useEffect } from 'react';
import { getApiUrl } from '../config/api';
import { isEntraConfigured, getMsalInstance, loginRequest } from '../config/authConfig';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);

  // ── Initialise: handle MSAL redirect response and restore local session ────
  useEffect(() => {
    let cancelled = false;

    async function init() {
      // 1. Handle Entra redirect (if this is a callback from Entra login)
      if (isEntraConfigured()) {
        try {
          const msal     = getMsalInstance();
          await msal.initialize();
          const response = await msal.handleRedirectPromise();

          if (response) {
            // Successfully returned from Entra — fetch the full RBAC user profile
            const appUser = await _fetchUserProfile(response.accessToken);
            // Persist before the cancelled check so the restore path can always
            // recover the session even if this render cycle is torn down.
            _storeOidcSession(response.accessToken, appUser);
            if (!cancelled) {
              setUser(appUser);
              setLoading(false);
              return;
            }
          }
        } catch (err) {
          console.error('[MSAL] Redirect handling error:', err);
        }
      }

      // 2. Restore session from stored token (legacy or previously stored OIDC)
      const storedToken = sessionStorage.getItem('oidc_token') ||
                          localStorage.getItem('token');
      const storedUser  = sessionStorage.getItem('oidc_user') ||
                          localStorage.getItem('user');

      const isLegacy = !sessionStorage.getItem('oidc_token') && !!localStorage.getItem('token');
      if (storedToken && storedUser && isLegacy) {
        // Username/password session: confirm the token is still accepted
        // before trusting the cached user. Tokens are signed and expire after
        // 12 hours (and every pre-2026-10-24 token is now invalid); without
        // this check an expired session looked signed in while every API
        // call failed.
        try {
          const appUser = await _fetchUserProfile(storedToken);
          localStorage.setItem('user', JSON.stringify(appUser));
          if (!cancelled) setUser(appUser);
        } catch {
          _clearSession();
        }
      } else if (storedToken && storedUser) {
        try {
          if (!cancelled) setUser(JSON.parse(storedUser));
        } catch {
          _clearSession();
        }
      } else if (storedToken && !storedUser) {
        // Token exists but user wasn't cached — re-verify with the backend.
        // This covers the case where oidc_user was never written (e.g. first
        // load after an older deploy) or was cleared by the browser.
        try {
          const appUser = await _fetchUserProfile(storedToken);
          _storeOidcSession(storedToken, appUser);
          if (!cancelled) setUser(appUser);
        } catch {
          _clearSession();
        }
      }

      if (!cancelled) setLoading(false);
    }

    init();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── OIDC silent token refresh ─────────────────────────────────────────────
  // Entra access tokens expire after ~1 hour. All API services read the token
  // directly from sessionStorage, so we proactively refresh it in the background.
  // This runs every 5 minutes and uses MSAL's acquireTokenSilent() which returns
  // a cached token if still valid or silently fetches a new one via the refresh token.
  useEffect(() => {
    if (!isEntraConfigured() || !user) return;

    async function refreshOidcToken() {
      try {
        const msal = getMsalInstance();
        await msal.initialize();
        const accounts = msal.getAllAccounts();
        if (accounts.length === 0) return;

        const response = await msal.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        });
        if (response && response.accessToken) {
          sessionStorage.setItem('oidc_token', response.accessToken);
        }
      } catch (err) {
        console.warn('[MSAL] Silent token refresh failed:', err.message);
        // If the refresh token itself is expired, the user needs to re-login.
        // InteractionRequiredAuthError signals this — clear session so the
        // next API 401 redirects to login cleanly.
        if (err.name === 'InteractionRequiredAuthError') {
          _clearSession();
          setUser(null);
        }
      }
    }

    // Refresh immediately on mount (covers tokens already near expiry)
    refreshOidcToken();
    const intervalId = setInterval(refreshOidcToken, 5 * 60 * 1000); // every 5 min
    return () => clearInterval(intervalId);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Helpers ─────────────────────────────────────────────────────────────────

  /** Fetch full RBAC user profile from the backend using the given Bearer token. */
  async function _fetchUserProfile(accessToken) {
    const res = await fetch(getApiUrl('/api/auth/verify'), {
      headers: { 'Authorization': `Bearer ${accessToken}` },
    });
    if (!res.ok) throw new Error('Failed to fetch user profile');
    const data = await res.json();
    return data.user || data;
  }

  function _storeOidcSession(accessToken, userObj) {
    sessionStorage.setItem('oidc_token', accessToken);
    sessionStorage.setItem('oidc_user', JSON.stringify(userObj));
    // Clear legacy localStorage tokens to avoid confusion
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }


  function _clearSession() {
    sessionStorage.removeItem('oidc_token');
    sessionStorage.removeItem('oidc_user');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }

  // ── Auth actions ─────────────────────────────────────────────────────────────

  /**
   * Sign in with Microsoft (Entra OIDC).
   * Redirects the browser to the Entra login page.
   * The app will resume in useEffect above when Entra redirects back.
   */
  const loginWithMicrosoft = async () => {
    if (!isEntraConfigured()) {
      console.warn('[MSAL] Entra is not configured — check REACT_APP_ENTRA_* env vars');
      return;
    }
    try {
      const msal = getMsalInstance();
      await msal.initialize();
      await msal.loginRedirect(loginRequest);
    } catch (err) {
      console.error('[MSAL] loginRedirect error:', err);
    }
  };

  /**
   * Sign in with username + password (local / break-glass accounts).
   */
  const login = async (username, password) => {
    try {
      const response = await fetch(getApiUrl('/api/auth/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Login failed');
      }

      const data = await response.json();

      // Legacy tokens go in localStorage (kept for backward compat)
      localStorage.setItem('user',  JSON.stringify(data.user));
      localStorage.setItem('token', data.token);

      setUser(data.user);
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Sign out. Clears session and, for OIDC users, calls Entra logout.
   */
  const logout = async () => {
    const wasOidc = !!sessionStorage.getItem('oidc_token');
    _clearSession();
    setUser(null);

    if (wasOidc && isEntraConfigured()) {
      try {
        const msal = getMsalInstance();
        await msal.initialize();
        await msal.logoutRedirect();
      } catch (err) {
        console.error('[MSAL] logoutRedirect error:', err);
      }
    }
  };

  /**
   * Returns the active Bearer token for API requests.
   * Prefers OIDC token (sessionStorage) over legacy (localStorage).
   */
  const getToken = () =>
    sessionStorage.getItem('oidc_token') || localStorage.getItem('token');

  // ── RBAC helpers (unchanged) ─────────────────────────────────────────────────

  const hasPermission = (permissionName) => {
    if (!user || !user.permissions) return false;
    return user.permissions.some(p => p.permission_name === permissionName);
  };

  const hasResourceAccess = (resource, action = 'read') => {
    if (!user || !user.permissions) return false;
    return user.permissions.some(
      p => p.resource === resource && (p.action === action || p.action === 'admin')
    );
  };

  const hasRole = (roleName) => {
    if (!user || !user.roles) return false;
    return user.roles.some(r => r.role_name === roleName);
  };

  const PRIVILEGED_ROLES = ['Super Admin', 'AU Admin', 'US Admin'];

  const isPrivileged = () =>
    PRIVILEGED_ROLES.some(r => hasRole(r));

  // The three domain checks below use exactly the backend's rule
  // (shared/middleware/auth.js): privileged role, or a domain_access row at
  // the right level. They used to also accept a role permission named after
  // the domain, which the backend never honours — such a user saw the domain
  // in the menu and then got "Access denied" on every request (2026-10-24).
  const canAccessDomain = (domain) => {
    if (!user) return false;
    if (isPrivileged()) return true;
    return !!(user.domainAccess && user.domainAccess.some(d => d.domain === domain));
  };

  const canEditDomain = (domain) => {
    if (!user) return false;
    if (isPrivileged()) return true;
    // Primary: check domain_access table entry with write/full level
    if (user.domainAccess) {
      const da = user.domainAccess.find(d => d.domain === domain);
      if (da && ['full', 'admin', 'write'].includes(da.access_level)) return true;
    }
    return false;
  };

  const canAdministerDomain = (domain) => {
    if (!user) return false;
    if (isPrivileged()) return true;
    // Primary: check domain_access table entry with admin/full level
    if (user.domainAccess) {
      const da = user.domainAccess.find(d => d.domain === domain);
      if (da && ['full', 'admin'].includes(da.access_level)) return true;
    }
    return false;
  };

  // Restricted screens (e.g. 'dto.staff_rates'): Super Admin, or a role
  // holding the permission 'screen:<key>'. AU / US Admin and domain admin
  // access do not count. Same rule as checkScreen() in the backend
  // (shared/middleware/auth.js); see add-screen-permissions-2026-10-25.sql.
  const canSeeScreen = (key) => {
    if (!user) return false;
    if (hasRole('Super Admin')) return true;
    return hasPermission(`screen:${key}`);
  };

  const canAccessEntity = (entityCode) => {
    if (!user) return false;
    if (hasRole('Super Admin')) return true;
    if (!user.entityAccess || user.entityAccess.length === 0) return true;
    if (user.entityAccess.some(e => e.entity_code === 'all')) return true;
    return user.entityAccess.some(e => e.entity_code === entityCode);
  };

  const getAccessibleEntities = () => {
    if (!user) return [];
    if (hasRole('Super Admin')) return ['all', 'AU', 'US'];
    if (!user.entityAccess || user.entityAccess.length === 0) return ['all', 'AU', 'US'];
    if (user.entityAccess.some(e => e.entity_code === 'all')) return ['all', 'AU', 'US'];
    return user.entityAccess.map(e => e.entity_code);
  };

  const value = {
    user,
    login,
    loginWithMicrosoft,
    logout,
    getToken,
    isAuthenticated: !!user,
    loading,
    isEntraConfigured,
    // RBAC helpers
    hasPermission,
    hasResourceAccess,
    hasRole,
    canAccessDomain,
    canEditDomain,
    canAdministerDomain,
    canSeeScreen,
    canAccessEntity,
    getAccessibleEntities,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
